// IE Hub - Contact Us Dialog Component
// Dialog for users to send support messages

import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Link,
  TextField
} from '@mui/material';
import { SupportService } from '@/shared/api/services';
import type { ButtonActionLogRequest } from '@/shared/api/types';

interface ContactUsDialogProps {
  open: boolean;
  onClose: () => void;
  userEmail?: string;
}

// Decide what to show in the success dialog based on API response + local context.
const buildSuccessHtml = (apiHtml: unknown, contactUsInfo: string): string => {
  const trimmed =
    typeof apiHtml === 'string'
      ? apiHtml.trim()
      : '';

  // If backend returns a non‑trivial message (not just an email), honour it.
  const emailLikePattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (trimmed && !emailLikePattern.test(trimmed)) {
    return trimmed;
  }

  const emailText = contactUsInfo || 'your registered email address';

  // Mirror legacy IE Hub copy and include where we’ll reply.
  return (
    `Thank you for contacting Customer Support at IE Hub. ` +
    `One of our customer care representatives will respond to your message within 24 hours during our opening times. ` +
    `Our Operating hours are Monday - Thursday 9am-6pm and Friday 9am-5pm.<br/><br/>` +
    `We’ll reply to <strong>${emailText}</strong>.`
  );
};

const ContactUsDialog: React.FC<ContactUsDialogProps> = ({ open, onClose, userEmail }) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successHtml, setSuccessHtml] = useState<string>('');
  const [error, setError] = useState('');

  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) return;
    // Log dialog open
    try {
      const { customerId, userId } = getIds();
      if (customerId && userId) {
        const payload: ButtonActionLogRequest = {
          buttonName: 'ContactUs',
          pageName: 'Support',
          subSection: 'OpenDialog',
          userId: String(userId),
          customerId: String(customerId)
        };
        SupportService.logButtonAction(payload);
      }
    } catch {}
    // Ensure the dialog is mounted before focusing
    const timer = setTimeout(() => {
      const textarea = messageRef.current;
      if (!textarea) return;
      textarea.focus({ preventScroll: true });
      if (textarea.value) {
        textarea.select();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please enter a message before submitting');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      const fallbackEmail = (() => {
        try {
          const raw = localStorage.getItem('customerData');
          const obj = raw ? JSON.parse(raw) : null;
          return obj?.email || obj?.emailAddress || '';
        } catch { return ''; }
      })();
      const contactUsInfo = userEmail || fallbackEmail || '';

      const res = await SupportService.sendContactUsMessage({ contactUsInfo, message });

      const apiHtml =
        (res as any)?.data?.contactUsInfo ??
        (res as any)?.contactUsInfo ??
        (res as any)?.data?.message ??
        (res as any)?.message;

      const htmlToShow = buildSuccessHtml(apiHtml, contactUsInfo);
      setSuccessHtml(htmlToShow);
      setSuccessOpen(true);
      try {
        onClose();
      } catch {}

      // Best-effort log
      try {
        const { customerId, userId } = getIds();
        if (customerId && userId) {
          const payload: ButtonActionLogRequest = {
            buttonName: 'ContactUs',
            pageName: 'Support',
            subSection: 'Submit',
            userId: String(userId),
            customerId: String(customerId)
          };
          SupportService.logButtonAction(payload);
        }
      } catch {}

      // Clear message but wait for user to close success dialog
      setMessage('');
      setError('');
    } catch (error) {
      console.error('Error submitting contact message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    try {
      const { customerId, userId } = getIds();
      if (customerId && userId) {
        const payload: ButtonActionLogRequest = {
          buttonName: 'ContactUs',
          pageName: 'Support',
          subSection: 'Cancel',
          userId: String(userId),
          customerId: String(customerId)
        };
        SupportService.logButtonAction(payload);
      }
    } catch {}
    setMessage('');
    setError('');
    onClose();
  };

  // Helper to pull ids from localStorage (mirrors other pages)
  const getIds = () => {
    let customerId: string | number | null = null;
    let userId: string | number | null = null;
    try {
      const storedCustomerRaw = typeof window !== 'undefined' ? localStorage.getItem('customerData') : null;
      const storedCustomer = storedCustomerRaw ? JSON.parse(storedCustomerRaw) : null;
      customerId = storedCustomer?.customerId || storedCustomer?.id || null;
    } catch {}
    try {
      const storedUserRaw = typeof window !== 'undefined' ? localStorage.getItem('userData') : null;
      const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
      userId = storedUser?.userId || null;
    } catch {}
    return { customerId, userId };
  };

  return (
    <>
    <Dialog
      open={open && !successOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: '8px',
          pt: '30px',
          px: '30px',
          pb: '20px',
          m: 2
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '27px' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '22px',
                fontWeight: 600,
                color: '#000',
                lineHeight: '20.952px'
              }}
            >
              Contact us
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 400,
                color: '#737373',
                lineHeight: '20.952px'
              }}
            >
              If you've checked the{' '}
              <Link
                href="https://www.iehub.co.uk/faq/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#737373',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: '#000'
                  }
                }}
              >
                FAQs
              </Link>
              {' '}and still need help, drop us a message in the box below letting us know how we can help.
              <br />
              <br />
              Operating hours are Monday - Thursday 9am-6pm and Friday 9am-5pm
            </Typography>
          </Box>

          {/* Message Field */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12.2px', pt: '5.8px' }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#0a0a0a',
                lineHeight: '14px'
              }}
            >
              Message
            </Typography>
            <TextField
              multiline
              rows={5}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (error) setError('');
              }}
              placeholder="Type your message here..."
              variant="outlined"
              fullWidth
              autoFocus
              inputRef={messageRef}
              error={!!error}
              helperText={error}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '40px'
            }}
          >
            <Button
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{
                border: '1px solid #d4d4d4',
                borderRadius: '8px',
                px: '22px',
                py: '5px',
                height: '100%',
                backgroundColor: '#fff',
                color: '#000',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '20.952px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #d4d4d4'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                backgroundColor: '#1a5053',
                border: '1px solid #1d7579',
                borderRadius: '8px',
                px: '22px',
                py: '5px',
                height: '100%',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: '20.952px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#134145'
                },
                '&:disabled': {
                  backgroundColor: '#e5e5e5',
                  color: '#a3a3a3',
                  border: '1px solid #d4d4d4'
                }
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>

    <Dialog
      open={successOpen}
      onClose={() => {
        setSuccessOpen(false);
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      keepMounted
      PaperProps={{
        sx: { borderRadius: '8px', p: 0, m: 2 }
      }}
    >
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 600 }}>Message sent</DialogTitle>
      <DialogContent>
        <Box sx={{ color: '#0a0a0a', fontSize: '14px' }}>
          <div dangerouslySetInnerHTML={{ __html: successHtml }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            onClick={() => {
              setSuccessOpen(false);
              onClose();
            }}
            sx={{
              backgroundColor: '#1a5053',
              border: '1px solid #1d7579',
              borderRadius: '8px',
              px: '18px',
              py: '5px',
              color: '#fff',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#134145' }
            }}
          >
            OK
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ContactUsDialog;

